import * as React from 'react';

// Match complete anchor tags with any attributes (for splitting text into parts)
// Matches: <a href="...">text</a>, <a class="btn" href="..." target="_blank">text</a>, etc.
// Uses [\s\S]*? to match any character including newlines (supports multiline anchor tags)
// The parentheses capture the entire anchor tag for use with split()
const PARTS_REGEX = /(<a\b[^>]*>[\s\S]*?<\/a>)/;

// Extract href and content from anchor tags with any attributes in any order
// Pattern breakdown: <a\b[^>]*href=(["'])([^"']+)\1[^>]*>([\s\S]*?)<\/a>
//   <a\b        - Match "<a" with word boundary (prevents matching "<aside" etc.)
//   [^>]*       - Match any attributes before href (class, id, etc.)
//   href=(["']) - Match href= followed by quote (group 1: captures " or ')
//   ([^"']+)    - Capture the href value (group 2: no quotes allowed inside)
//   \1          - Backreference to match the same quote type as group 1
//   [^>]*       - Match any attributes after href (target, rel, etc.)
//   >([\s\S]*?)<\/a> - Capture inner content (group 3) including newlines until closing tag
// Examples: <a href="url">text</a>, <a href='url'>text</a>, <a class="btn" href="url">content</a>
const LINK_REGEX = /<a\b[^>]*href=(["'])([^"']+)\1[^>]*>([\s\S]*?)<\/a>/;

// Match URLs with protocol (http://example.com) or domain-like patterns (example.com, www.example.com)
const URL_REGEX = /(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/;

interface LinkifyProps {
  text: string;
}

// Sanitize URL to prevent XSS and ensure it's a valid URL
// Handles relative URLs (/, ./, ../, #, ?) and absolute URLs with proper validation
const sanitizeURL = (url: string): string | null => {
  try {
    // Handle relative URLs - these are safe within the same origin
    if (url.match(/^(\/[^/]|\.\/|\.\.\/|#|\?)/)) {
      return url;
    }

    // Handle URLs with protocols or protocol-like patterns
    if (url.includes('://') || url.includes(':')) {
      // Only allow http:// and https:// protocols, reject everything else
      if (!url.match(/^https?:\/\//)) {
        return null; // Reject ftp://, javascript:, data:, mailto:, etc.
      }
      return new URL(url).toString();
    }

    // Protocol-less absolute URLs (e.g., google.com, www.example.com)
    // Prepend https:// and parse (no validation needed since we control the protocol)
    return new URL(`https://${url}`).toString();
  } catch {
    return null;
  }
};

// Check if text contains existing HTML anchor tags
const containsHTMLLinks = (text: string): boolean => {
  return PARTS_REGEX.test(text);
};

export const Linkify: React.FC<LinkifyProps> = ({ text }) => {
  // If text contains HTML links, parse HTML; otherwise auto-detect URLs
  if (containsHTMLLinks(text)) {
    // HTML parsing mode: parse existing HTML <a> tags
    const parts = text.split(PARTS_REGEX);
    return (
      <>
        {parts.map((part, key) => {
          const matches = part.match(LINK_REGEX);
          if (!matches) {
            return <span key={key}>{part}</span>;
          }
          const [, , href, content] = matches;
          // Sanitize href from existing HTML links too
          const sanitizedURL = sanitizeURL(href);
          if (sanitizedURL) {
            return (
              <a key={key} href={sanitizedURL}>
                {content}
              </a>
            );
          } else {
            // If URL is invalid/malicious, render content without link
            return <span key={key}>{content}</span>;
          }
        })}
      </>
    );
  }

  // Auto-detect raw URLs and make them clickable
  // Use fresh global regex instance for split to avoid lastIndex issues
  const parts = text.split(new RegExp(URL_REGEX.source, 'g'));
  return (
    <>
      {parts.map((part, key) => {
        if (URL_REGEX.test(part)) {
          const sanitizedURL = sanitizeURL(part);
          if (sanitizedURL) {
            return (
              <a key={key} href={sanitizedURL} target="_blank" rel="noopener noreferrer" className="text-primary">
                {part}
              </a>
            );
          } else {
            // If URL is invalid/malicious, render as plain text
            return part;
          }
        }
        return part;
      })}
    </>
  );
};
